import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useUrlState from '../../../hooks/use-url-state';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import useStation from '../../../hooks/stations/query/useStation';
import { IStation } from '../../../ts/interface/station.interface';
import { useMaintenanceLogsRelayQuery } from '../../../hooks/maintenance/query/V2/useMaintenanceLogsRelay';
import { mapMaintenanceLogsToLegacy } from '../../../hooks/maintenance/query/V2/maintenanceMappers';
import { history, toTitleCase } from '../../../utils';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import {
  MovaticFieldGroupAsyncSelect,
  MovaticFieldGroupSelectFilter,
} from '../../../components/Redesing/movatic-field-group';
import { taskColumnsV2 } from '../../../tableColumns/maintenanceColumns';
import useOrganization from '../../../hooks/use-organization';
import useSystemList from '../../../hooks/system/hook/useSystemList';
import { LocationStatus } from '../../../ts/enums';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';

// Map legacy sort keys to GraphQL sort field names
const sortKeyMap: Record<string, string> = {
  created_on: 'CREATED_ON',
  task_name: 'MAINTENANCE_TASK_OLD_ID',
  admin_name: 'ADMIN_USER_ID',
  time_tracking: 'TIME_TRACKING',
};

function OrganizationMaintenanceLog() {
  const organizationInfo = useOrganization();
  const { systemList } = useSystemList(organizationInfo?.id);
  const [open, setOpen] = useState(false);
  const [exportData, setExportData] = useState<boolean>(false);
  const [chips, setChips] = useState<SearchChip[]>([]);
  const [searchLocation, setSearchLocation] = useState<string>('');

  const [pageNumber, setPageNumber] = useState(0);
  // Keyset (cursor) pagination state. The backend returns opaque keyset cursors,
  // so we feed the previous page's endCursor into `after` and accumulate pages
  // locally, letting PaginationTable slice the requested page.
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [accumulatedLogs, setAccumulatedLogs] = useState<any[]>([]);
  const [pageState, setPageState, isComplete, isRestored] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    system: '',
    selectedSystem: [] as Option[],
    sortingConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
    locationFilter: { value: '', label: '' } as Option | null,
    location: '' as string | null,
  });

  const debouncedSearchTerm = useSearchDebounce(pageState.search);

  const { data: stationsData, isLoading: isLoadingLocations } = useStation(
    {},
    {
      organization_id: organizationInfo?.id,
      status: LocationStatus.active,
      pattern: searchLocation,
    }
  );

  const stations = useMemo(() => stationsData?.data || [], [stationsData]);

  useEffect(() => {
    if (isComplete && !isRestored) {
      const locationValues: IStation[] = stations.filter((station: IStation) =>
        pageState.location?.includes(station.id.toString())
      );
      const locationChips = locationValues.map((location) => ({
        label: 'Location',
        field: 'location',
        value: location.id.toString(),
        displayValue: location.name,
      }));
      setPageState((prevStation) => ({
        ...prevStation,
        locationFilter: {
          value: locationValues[0]?.id.toString(),
          label: locationValues[0]?.name,
        },
      }));
      if (Boolean(pageState.selectedSystem?.length)) {
        setChips((prevChips) => [
          ...prevChips,
          ...(pageState.selectedSystem?.map((item) => ({
            label: 'System',
            field: 'system',
            value: item.value,
            displayValue: item.label,
          })) || []),
        ]);
      }
      setChips(locationChips);
    }
  }, [
    isRestored,
    isComplete,
    pageState.location,
    setPageState,
    pageState.selectedSystem,
    stations,
  ]);

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const field =
      sortKeyMap[pageState.sortingConfig?.key || 'created_on'] || 'CREATED_ON';
    const order =
      pageState.sortingConfig?.direction?.toUpperCase() === 'DESC'
        ? 'DESC'
        : 'ASC';
    return [{ field: field as any, order: order as 'ASC' | 'DESC' }];
  }, [pageState.sortingConfig]);

  const {
    data: logsDataRaw,
    count: totalLogs,
    isLoading: loadingLogs,
    isRefetching,
    hasMore,
    endCursor,
  } = useMaintenanceLogsRelayQuery(
    {
      organizationId: organizationInfo?.id || undefined,
      locationId: pageState.location || undefined,
      search: debouncedSearchTerm || undefined,
    },
    {
      limit: pageState.sizePerPage,
      sort: sortConfig,
      enabled: isComplete && !!organizationInfo?.id,
      after: afterCursor || undefined,
    }
  );

  // Any change to the query inputs (filters, search, sort, page size) invalidates
  // the accumulated cursor pages and returns to the first page.
  const resetPagination = useCallback(() => {
    setAccumulatedLogs([]);
    setAfterCursor(null);
    setPageNumber(0);
  }, []);

  const filteredCompletedTasks = useMemo(
    () => (logsDataRaw ? mapMaintenanceLogsToLegacy(logsDataRaw) : []),
    [logsDataRaw]
  );

  // Accumulate cursor pages so the table can slice the requested page locally.
  // Appends when advancing (afterCursor set); replaces on the first page.
  useMemo(() => {
    if (!filteredCompletedTasks.length && !afterCursor) {
      setAccumulatedLogs([]);
      return;
    }
    if (filteredCompletedTasks.length) {
      if (afterCursor) {
        setAccumulatedLogs((prev) => [...prev, ...filteredCompletedTasks]);
      } else {
        setAccumulatedLogs(filteredCompletedTasks);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCompletedTasks]);

  const logItems =
    accumulatedLogs.length > 0 ? accumulatedLogs : filteredCompletedTasks;

  const handleChipsChange = useCallback(
    (
      values: string[],
      list: { label: string; value: string }[],
      metadata: { label: string; value: string }
    ): void => {
      resetPagination();
      setPageState((prevState) => ({
        ...prevState,
        [metadata.value]: values.join(','),
      }));

      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== metadata.value) {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }

        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = list.find((option) => option.value.toString() === value);
            newChips.push({
              label: metadata.label,
              field: metadata.value,
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    [setPageState, resetPagination]
  );

  const stationOptions = useMemo(() => {
    return stations.map((item: IStation) => {
      return {
        value: item.id,
        label: toTitleCase(item.name),
      };
    });
  }, [stations]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });
      resetPagination();
      setPageState((prevState) => ({
        ...prevState,
        locationFilter: null,
        location: '',
      }));
    },
    [setPageState, resetPagination]
  );

  const columnsDis = useMemo(() => {
    const unitTitle = '';
    const isNewHardware = true;
    const showSystemColumn = true;
    const stationsTitle = 'locations';
    return taskColumnsV2(unitTitle, isNewHardware, stationsTitle, showSystemColumn);
  }, []);

  return (
    <PaginationTable
      showFilter
      totalItems={totalLogs || 0}
      isLoading={
        (loadingLogs && !logItems.length) ||
        !isComplete ||
        (pageNumber > 0 && logItems.length <= pageNumber * pageState.sizePerPage)
      }
      isLoadingCache={isRefetching}
      hasMore={hasMore}
      padding={{ pt: 1, pl: 0, pr: 1 }}
      exportData={exportData}
      csvFileName={'Maintenance_logs.csv'}
      onExport={() => setExportData(false)}
      localSearch={false}
      buttons={[
        {
          label: 'Export',
          startIcon: <FileDownloadIcon />,
          onClick: () => setExportData(true),
        },
        {
          label: 'Add',
          onClick: () => history.push('/maintenance/log/add'),
          startIcon: <AddIcon />,
        },
      ]}
      onApplyFilter={() => {
        if (
          JSON.stringify(pageState.locationFilter) !== JSON.stringify(pageState.location)
        ) {
          handleChipsChange([pageState.locationFilter?.value || ''], stationOptions, {
            label: 'Location',
            value: 'location',
          });
        }

        if (
          JSON.stringify(pageState.selectedSystem) !== JSON.stringify(pageState.system)
        ) {
          handleChipsChange(
            pageState.selectedSystem.map((item) => item.value.toString()),
            systemList,
            { label: 'System', value: 'system' }
          );
        }
      }}
      chips={chips}
      handleChipDelete={handleChipDelete}
      spaceFilters={
        <>
          <MovaticFieldGroupAsyncSelect
            dataId="location-single-select"
            label={'Location'}
            description="Select the location to filter the maintenance logs."
            isLoading={isLoadingLocations}
            name={'location_select'}
            id={'location_select'}
            options={stationOptions}
            showClearButton={true}
            value={!pageState.locationFilter?.value ? null : pageState.locationFilter}
            onChange={(
              event: React.SyntheticEvent,
              newValue: { value: string; label: string }
            ) => {
              setPageState((prevState) => ({
                ...prevState,
                locationFilter: newValue,
              }));
            }}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            isOptionEqualToValue={(option: any, value: any) =>
              option.value === value.value
            }
            getOptionText={(option: any) => option.label}
            handleSearch={(search) => setSearchLocation(search.target.value)}
          />
          <MovaticFieldGroupSelectFilter
            label={'System'}
            value={pageState.selectedSystem}
            onChange={(event: React.SyntheticEvent, newValue: any[]) => {
              setPageState((prevState) => ({
                ...prevState,
                selectedSystem: newValue.map((item) => ({
                  label: item.label,
                  value: item.value,
                })),
              }));
            }}
            options={systemList || []}
          />
        </>
      }
      customKey="gid"
      onRowCLick={(id: string) => history.push(`/maintenance/log/${id}`)}
      sortBy={pageState?.sortingConfig?.key}
      onSortChange={(sort) => {
        resetPagination();
        setPageState((prevState) => ({
          ...prevState,
          sortingConfig: sort as ISortConfig,
        }));
      }}
      columns={columnsDis as IColumn[]}
      items={logItems}
      searchPlaceholder={`Search logs`}
      page={pageNumber}
      rowsPerPage={pageState.sizePerPage}
      onPageChange={(page, newPage) => {
        // Advancing past the accumulated pages: fetch the next keyset page.
        if (logItems.length && logItems.length / pageState.sizePerPage < newPage + 1) {
          setAfterCursor(endCursor || null);
        }
        setPageNumber(newPage);
      }}
      onRowsPerPageChange={(pageSizeString) => {
        resetPagination();
        setPageState((prevState) => ({
          ...prevState,
          sizePerPage: Number(pageSizeString.target.value),
        }));
      }}
      handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
        resetPagination();
        setPageState((prevState) => ({
          ...prevState,
          search: e.target.value,
        }));
      }}
      searchText={pageState.search}
    />
  );
}

export default OrganizationMaintenanceLog;
